import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, Calendar, Edit2 } from 'lucide-react';
import { differenceInMinutes } from 'date-fns';
import { useCompanyTimezone } from '@/hooks/useCompanyTimezone';
import { formatEntryDate, formatEntryTime } from '@/lib/timesheetTime';

interface TimeEntry {
  id: string;
  clock_in: string;
  clock_out: string | null;
  entry_type?: 'automatic' | 'manual';
}

interface TimesheetCardProps {
  entry: TimeEntry;
}

export function TimesheetCard({ entry }: TimesheetCardProps) {
  const timezone = useCompanyTimezone();

  const formatDuration = (clockIn: string, clockOut: string | null) => {
    if (!clockOut) return 'In Progress';
    
    const minutes = differenceInMinutes(new Date(clockOut), new Date(clockIn));
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours === 0) {
      return `${remainingMinutes}m`;
    }
    
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  return (
    <Card className="w-full">
      <CardContent className="p-4">
        <div className="space-y-3">
          {/* Date and Manual Badge */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="font-semibold">
                {formatEntryDate(entry.clock_in, timezone)}
              </span>
            </div>
            {entry.entry_type === 'manual' && (
              <Badge variant="outline" className="text-xs">
                <Edit2 className="h-3 w-3 mr-1" />
                Manual
              </Badge>
            )}
          </div>

          {/* Times */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-muted-foreground">Clock In</span>
              <p className="font-medium">
                {formatEntryTime(entry.clock_in, timezone)}
              </p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Clock Out</span>
              <p className="font-medium">
                {entry.clock_out 
                  ? formatEntryTime(entry.clock_out, timezone)
                  : '-'
                }
              </p>
            </div>
          </div>

          {/* Total Hours */}
          <div className="pt-2 border-t">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Hours</span>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <Badge variant={entry.clock_out ? "default" : "secondary"}>
                  {formatDuration(entry.clock_in, entry.clock_out)}
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}